<template>
    <div class="lg:grid lg:gap-3 lg:grid-cols-12 xl:gap-6">
        <Head>
            <title>{{ post.text | trim(100) }}</title>
        </Head>

        <div class="lg:col-span-8">
            <heading class="lg:hidden">
                <template #heading>
                    <div class="flex justify-between">
                        <Link :href="route('dashboard')" class="flex items-center hover:text-indigo-600 space-x-1">
                            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 17l-5-5m0 0l5-5m-5 5h12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                            </svg>
                            <span>Home</span>
                        </Link>
                        <div class="flex justify-center text-pink-500 font-display text-4xl font-bold">
                            <span class="currency">{{ post.pledgedAmount | toLocaleString }}</span>
                        </div>
                    </div>
                </template>
            </heading>

            <div v-if="post.isApproved" class="sm:hidden bg-green-500 text-xl font-bold text-white px-6 py-3">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6 mr-2 inline-block">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Challenge Completed
            </div>

            <!-- Content -->
            <div class="items-top flex flex-col justify-start sm:pt-0">
                <alert-success :alert="alert" class="mb-5" />

                <!--Challenge is nt live warning-->
                <!--<alert v-if="post.pledgedAmount === 0" class="bg-yellow-400 mb-2 mx-2 sm:mx-0">-->
                <!--    <template #icon>-->
                <!--        <svg class="h-10 w-10 text-yellow-800" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">-->
                <!--            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />-->
                <!--        </svg>-->
                <!--    </template>-->
                <!--    <template #text>-->
                <!--        <div class="sm:text-2xl font-bold text-yellow-800">This challenge is not active. You must pledge at least 10 tokens.</div>-->
                <!--    </template>-->
                <!--</alert>-->
                <div class="block sm:hidden mb-2 ml-2 mr-2" v-if="hasPendingCompetitors">
                    <challenge-request-button :href="route('challengeRequests.index', { post: post })" class="disabled:bg-yellow-200 bg-yellow-500 hover:bg-yellow-700 active:bg-yellow-800 ring-yellow-500">
                        <span class="absolute left-3 inline-flex items-center mr-1 px-3 py-0.5 text-white font-medium bg-yellow-600 rounded-full">
                            <span class="flex absolute inset-0 z-0">
                                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-200 opacity-75"></span>
                            </span>
                            {{ post.pendingCompetitors.length }}
                        </span>
                        Pending {{ "video" | pluralize(post.pendingCompetitors.length) }}
                    </challenge-request-button>
                </div>

                <div class="sm:space-y-4 sm:content-top">
                    <!-- Post Content -->
                    <section aria-labelledby="challenge-details">
                        <div class="relative bg-white dark:bg-slate-800 shadow lg:rounded-lg overflow-hidden">
                            <!-- Featured Image -->
                            <div v-if="!competitor" class="aspect-w-10 aspect-h-7 block p-0 w-full h-48 shadow-inner sm:overflow-hidden lg:rounded-t-lg" @click="galleryThumbnail = post.featuredImageHtml" v-html="post.featuredImageHtml"></div>
                            <!-- Video Player-->
                            <video-player v-if="competitor && competitor.video.playlistUrl" :options="videoPlayerOptions"></video-player>

                            <div v-if="competitor && !competitor.video.playlistUrl" class="w-full h-72 sm:h-96 bg-green-50 flex justify-center items-center">
                                <span class="text-lg sm:text-2xl text-green-500 animate-pulse uppercase">Processing video...</span>
                            </div>

                            <!-- Thumbnail Gallery -->
                            <ul v-show="post.thumbs.length > 1" class="grid gap-1 grid-cols-3 p-1 md:grid-cols-3 lg:grid-cols-6">
                                <li v-for="(thumb, i) in post.thumbs" :key="i" class="relative">
                                    <div class="group aspect-w-10 aspect-h-7 block w-full focus-within:outline-none overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2" @click="galleryThumbnail = post.images[i]" v-html="thumb"></div>
                                </li>
                            </ul>

                            <!-- User Info -->
                            <div class="flex items-center my-4 px-4">
                                <div class="flex-shrink-0">
                                    <avatar :user="post.user" class="w-12 h-12"></avatar>
                                </div>

                                <div class="ml-5 mr-auto">
                                    <h1 class="text-gray-700 dark:text-slate-400 font-medium">
                                        {{ post.user.tag }}
                                    </h1>
                                    <p class="text-gray-500 dark:text-slate-400 text-xs font-medium">
                                        <time :datetime="post.createdAt">
                                            {{ post.createdAt | dateFormat("MMMM D, YYYY h:mm A") }}
                                        </time>
                                    </p>
                                </div>

                                <!-- Dropdown -->
                                <div class="has-tooltip manual-tooltip">
                                    <jet-dropdown v-if="$page.props.auth.check" align="right" width="48">
                                        <template #trigger>
                                            <button aria-expanded="false" aria-haspopup="true" class="flex items-center -m-px p-1 hover:text-blue-500 focus:text-blue-600 text-gray-500 hover:bg-gray-100 focus:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-600" type="button">
                                                <span class="sr-only">Open options</span>
                                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                </svg>
                                            </button>
                                        </template>

                                        <template #content>
                                            <div class="flex flex-col p-1 space-y-1">
                                                <!-- Follow Post -->
                                                <!--<Link v-if="!post.isChallenger" :href="post.favoriteUrl"-->
                                                <!--              :preserve-scroll="true"-->
                                                <!--              as="button"-->
                                                <!--              class="flex rounded w-full px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-blue-500"-->
                                                <!--              method="post"-->
                                                <!--              role="menuitem">-->
                                                <!--    &lt;!&ndash; Heroicon name: solid/star &ndash;&gt;-->
                                                <!--    <svg-->
                                                <!--        :class="{'text-gray-400':!post.favorited,'text-yellow-500':post.favorited}"-->
                                                <!--        aria-hidden="true" class="mr-3 h-5 w-5" fill="currentColor"-->
                                                <!--        viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">-->
                                                <!--        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />-->
                                                <!--    </svg>-->
                                                <!--    <span v-if="!post.favorited">Follow</span>-->
                                                <!--    <span v-if="post.favorited">Unfollow</span>-->
                                                <!--</Link>-->

                                                <Link
                                                    :href="
                                                        route('posts.show', {
                                                            username: post.user.username,
                                                            post: post.id,
                                                        })
                                                    "
                                                    class="flex px-4 py-2 w-full hover:text-blue-500 text-gray-500 dark:text-slate-400 text-sm hover:bg-gray-100 rounded"
                                                    role="menuitem"
                                                    @click.prevent=";[copyToClipboard($event), $refs.copyTextTooltip.showTooltip($event)]"
                                                >
                                                    <svg class="mr-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                    </svg>
                                                    <span>Copy Link</span>
                                                </Link>

                                                <!-- Edit Post -->
                                                <!--<Link v-if="post.isChallenger" :href="route('posts.edit', post.id)"-->
                                                <!--              class="flex rounded px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 hover:text-gray-900"-->
                                                <!--              role="menuitem">-->
                                                <!--    <svg class="mr-3 h-5 w-5 text-gray-400"-->
                                                <!--         fill="none" stroke="currentColor" viewBox="0 0 24 24"-->
                                                <!--         xmlns="http://www.w3.org/2000/svg">-->
                                                <!--        <path-->
                                                <!--            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"-->
                                                <!--            stroke-linecap="round"-->
                                                <!--            stroke-linejoin="round"-->
                                                <!--            stroke-width="2" />-->
                                                <!--    </svg>-->
                                                <!--    <span>Edit</span>-->
                                                <!--</Link>-->
                                            </div>
                                            <div class="p-1">
                                                <!-- Delete Post -->
                                                <a v-if="post.isChallenger" class="flex px-4 py-2 text-red-700 hover:text-red-900 text-sm hover:bg-red-100 rounded" href="#" role="menuitem" @click.prevent="confirmDelete">
                                                    <!--<svg class="mr-3 h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">-->
                                                    <!--    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />-->
                                                    <!--</svg>-->
                                                    <svg class="mr-3 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                    </svg>
                                                    <span>Archive</span>
                                                </a>
                                                <report-post v-if="!post.isChallenger" :post="post" />
                                            </div>
                                        </template>
                                    </jet-dropdown>
                                    <tooltip ref="copyTextTooltip" background-color="bg-blue-500 border-blue-500 text-white">
                                        <div class="flex items-center space-x-1">
                                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                            </svg>
                                            <div class="leading-none">copied</div>
                                        </div>
                                    </tooltip>
                                </div>
                            </div>

                            <!-- Title & Description -->
                            <div class="my-4 px-4">
                                <div class="text-gray-800 dark:text-slate-400 text-sm sm:text-base">
                                    <div ref="postHtml" :class="[readMore ? 'line-clamp-none' : 'line-clamp-4']" v-html="post.html"></div>
                                </div>
                            </div>

                            <!-- Read More -->
                            <div v-if="isLineClamped && !readMore">
                                <a
                                    :class="[readMore ? 'flex-col' : 'flex-col-reverse']"
                                    class="group flex items-center justify-center text-center text-gray-500 dark:text-slate-400 focus:text-indigo-400 hover:text-indigo-500 dark:hover:text-slate-300 text-sm font-light"
                                    href="#"
                                    @click.prevent="toggleReadMore"
                                >
                                    <svg v-show="!readMore" class="w-5 h-5 animate-bounce group-hover:animate-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M19 9l-7 7-7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" />
                                    </svg>
                                    <svg v-show="readMore" class="-mb-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M5 15l7-7 7 7" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" />
                                    </svg>
                                    <span>{{ readMore ? "Read Less" : "Read More" }}</span>
                                </a>
                            </div>

                            <div class="flex items-center justify-between px-5 py-3 bg-gray-50 dark:bg-slate-900/50 rounded-b-lg">
                                <div class="flex items-center text-gray-500 text-sm leading-5 space-x-3">
                                    <!-- Likes -->
                                    <like :likeable="post"></like>

                                    <span class="text-gray-500 font-medium">&middot;</span>

                                    <!-- Comments -->
                                    <div class="has-tooltip">
                                        <a class="group flex items-center rounded-full space-x-1" href="#comments">
                                            <svg class="w-6 h-6 text-gray-500 group-hover:text-gray-700 font-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" />
                                            </svg>
                                            <div class="flex-shrink-0 group-hover:text-gray-700">
                                                {{ post.commentsCount }}
                                            </div>
                                        </a>
                                        <tooltip v-if="post.commentsCount">
                                            Show
                                            {{ post.commentsCount | abbr }}
                                            {{ "comment" | pluralize(post.commentsCount) }}</tooltip
                                        >
                                    </div>
                                </div>

                                <!-- Share Post -->
                                <button class="has-tooltip flex items-center -m-px p-2 hover:text-blue-500 focus:text-blue-600 text-gray-500 hover:bg-gray-100 focus:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500" @click="showShareModal = true">
                                    <!-- Heroicon name: solid/share -->
                                    <svg class="w-5 h-5 transform-gpu -scale-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                    </svg>
                                    <span class="sr-only">Share</span>
                                    <tooltip>Share</tooltip>
                                </button>
                            </div>
                        </div>
                    </section>

                    <!-- sm:hidden -->
                    <div class="lg:hidden">
                        <!-- Activity Feed -->
                        <div class="flow-root flex-col-reverse sm:mt-4">
                            <div v-if="post.userPledgeCount > 0" class="hidden sm:block p-4 bg-white dark:bg-slate-800 sm:rounded-lg mb-2 shadow">
                                <div class="flex items-start">
                                    <!-- Circles and connecting line on the left -->
                                    <div class="mr-4 flex flex-col items-center justify-start">
                                        <!-- Top circle -->
                                        <div class="bg-gray-200 rounded-full h-5 w-5 mt-1 mb-2"></div>
                                        <!-- Connecting line -->
                                        <div class="h-2.5 w-1 bg-gray-300"></div>
                                        <!-- Bottom circle -->
                                        <div class="bg-gray-200 rounded-full h-5 w-5 mt-2"></div>
                                    </div>

                                    <!-- Text on the right -->
                                    <div>
                                        <p class="text-gray-700 text-lg">
                                            You've pledged <span class="font-bold">{{ post.userPledgeCount }}</span> {{ "time" | pluralize(post.userPledgeCount) }}.
                                        </p>
                                        <div class="w1 h-4"></div>
                                        <p class="text-gray-700 text-lg">
                                            Total amount: <span class="font-bold">${{ post.userPledgeTotal | toLocaleString }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <pledges :post="post" />

                            <div v-if="post.userPledgeCount > 0" class="sm:hidden -mt-3 mb-2 p-4 bg-white dark:bg-slate-800 sm:rounded-lg shadow">
                                <div class="flex items-start">
                                    <!-- Circles and connecting line on the left -->
                                    <div class="mr-4 flex flex-col items-center justify-start">
                                        <!-- Top circle -->
                                        <div class="bg-gray-200 rounded-full h-5 w-5 mt-1 mb-2"></div>
                                        <!-- Connecting line -->
                                        <div class="h-2.5 w-1 bg-gray-300"></div>
                                        <!-- Bottom circle -->
                                        <div class="bg-gray-200 rounded-full h-5 w-5 mt-2"></div>
                                    </div>

                                    <!-- Text on the right -->
                                    <div>
                                        <p class="text-gray-700 text-lg">
                                            You've pledged <span class="font-bold">{{ post.userPledgeCount }}</span> {{ "time" | pluralize(post.userPledgeCount) }}.
                                        </p>
                                        <div class="w1 h-4"></div>
                                        <p class="text-gray-700 text-lg">
                                            Total amount: <span class="font-bold">${{ post.userPledgeTotal | toLocaleString }}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Comments -->
                    <comments :post="post" />
                </div>
            </div>
        </div>

        <!-- Aside -->
        <div class="hidden lg:block lg:col-span-4 pr-1 sm:pr-6">
            <div class="sticky top-8 space-y-4">
                <!-- Approved Banner -->
                <div v-if="post.isApproved" class="bg-green-500 text-xl font-bold text-white px-6 py-3 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="h-6 w-6 mr-2 inline-block">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Challenge Completed
                </div>

                <!-- Display pledge information only if user has pledged -->
                <div v-if="post.userPledgeCount > 0" class="mt-4 p-4 bg-white dark:bg-slate-800 rounded-lg shadow">
                    <div class="flex items-start">
                        <!-- Circles and connecting line on the left -->
                        <div class="mr-4 flex flex-col items-center justify-start">
                            <!-- Top circle -->
                            <div class="bg-gray-200 dark:bg-slate-700 rounded-full h-5 w-5 mt-1 mb-2"></div>
                            <!-- Connecting line -->
                            <div class="h-2.5 w-1 bg-gray-300 dark:bg-slate-600"></div>
                            <!-- Bottom circle -->
                            <div class="bg-gray-200 dark:bg-slate-700 rounded-full h-5 w-5 mt-2"></div>
                        </div>

                        <!-- Text on the right -->
                        <div>
                            <p class="text-gray-700 dark:text-slate-400 text-lg">
                                You've pledged <span class="font-bold">{{ post.userPledgeCount }}</span> {{ "time" | pluralize(post.userPledgeCount) }}.
                            </p>
                            <div class="w1 h-4"></div>
                            <p class="text-gray-700 dark:text-slate-400 text-lg">
                                Total amount: <span class="font-bold">${{ post.userPledgeTotal | toLocaleString }}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Pledges -->
                <pledges :post="post" :options="pledgeModal" />
            </div>
        </div>

        <!-- Image Modal-->
        <media-modal :image.sync="galleryThumbnail" :images="post.images" :show="showGalleryThumbnail" max-height="full" @close="showGalleryThumbnail = false" />

        <!-- Contribute Tokens Modal -->
        <!--<pledge-tokens-modal v-if="showPledgeTokensModal" :show.sync="showPledgeTokensModal" />-->

        <!--Buy Tokens Modal-->
        <!--<buy-tokens-modal :amount="buyMoreTokensAmount" :show.sync="showBuyTokensModal" />-->

        <!-- Delete Post Modal -->
        <!--<delete-post-modal :post.sync="deletingPost"></delete-post-modal>-->

        <!-- Report Post Modal -->
        <!--<report-post-modal :post="post" :show.sync="showReportModal"></report-post-modal>-->

        <!-- Share Modal -->
        <share-modal :post="post" :show.sync="showShareModal" />
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import JetDialogModal from "@/Jetstream/DialogModal"
import JetSecondaryButton from "@/Jetstream/SecondaryButton"
import JetButton from "@/Jetstream/Button"
import Comments from "@/Comments/Comments"
import ImageModal from "@/Components/ImageModal"
import JetDropdown from "@/Jetstream/Dropdown"
import DeletePostModal from "@/Pages/Post/DeletePostModal"
import PledgeTokensModal from "@/Pages/Wallet/PledgeTokensModal"
import Breadcrumbs from "@/Components/Breadcrumbs"
import ReportPost from "@/Pages/Post/ReportPost.vue"
import AlertSuccess from "@/Components/Alerts/Success"
import Pledges from "@/Pages/Post/Pledges"
import LinkButton from "@/Components/Buttons/LinkButton"
import CommentForm from "@/Comments/CommentForm"
import ShareModal from "@/Components/ShareModal"
import Like from "@/Components/Like"
import Tooltip from "@/Components/Tooltip"
import MediaModal from "@/Components/MediaModal"
import BuyTokensModal from "@/Pages/Wallet/BuyTokensModal"
import Heading from "@/Components/Heading"
import VideoPlayer from "@/Components/VideoPlayer"
import Alert from "@/Components/Alerts/Alert"
import ChallengeRequestButton from "@/Pages/Post/ChallengeRequestButton.vue"

export default {
    name: "Post",

    layout: AppLayout,

    components: {
        ReportPost,
        ChallengeRequestButton,
        Alert,
        Heading,
        BuyTokensModal,
        MediaModal,
        Tooltip,
        Like,
        ShareModal,
        VideoPlayer,
        CommentForm,
        LinkButton,
        Pledges,
        AlertSuccess,
        Breadcrumbs,
        PledgeTokensModal,
        DeletePostModal,
        JetDropdown,
        ImageModal,
        Comments,
        AppLayout,
        JetDialogModal,
        JetSecondaryButton,
        JetButton,
    },

    props: {
        post: Object,
    },

    mounted() {
        this.$nextTick(() => {
            if (this.$refs.postHtml) {
                this.isLineClamped = this.$refs.postHtml.scrollHeight > this.$refs.postHtml.clientHeight
            }
        })
    },

    computed: {
        images() {
            return this.post.images
        },
        competitor() {
            return this.post.competitor ?? null
        },
        hasPendingCompetitors() {
            var pendingCompetitors = this.post.pendingCompetitors
            if (pendingCompetitors) {
                return pendingCompetitors.length
            }
        },
        video() {
            return this.competitor.video ?? null
        },
        videoPlayerOptions() {
            if (!this.competitor) return null

            return {
                sources: [{ src: this.video.playlistUrl }],
                fluid: true,
                autoplay: false,
                aspectRatio: this.video.formattedAspectRatio,
                poster: this.video.frames[0],
            }
        },
    },

    watch: {
        galleryThumbnail(thumbnail) {
            this.showGalleryThumbnail = !!thumbnail
        },

        showGalleryThumbnail(bool) {
            if (!bool) {
                this.galleryThumbnail = null
            }
        },
    },

    remember: {
        data: ["post"],
        key() {
            return `Post:${this.post.id}`
        },
    },

    data() {
        return {
            alert: this.$page.props.alert,
            isLineClamped: false,
            readMore: false,
            showGalleryThumbnail: false,
            galleryThumbnail: null,
            favoriteForm: this.$inertia.form({
                _method: "POST",
            }),
            deletingPost: null,
            reportingPost: null,
            breadcrumbs: [{ text: "Challenges", url: this.route("user.posts.list") }, { text: this.post.title }],
            showReportModal: false,
            showShareModal: false,
            showBuyTokensModal: false,
            buyMoreTokensAmount: 0,
            pledgeModal: this.$page.props.pledgeModal,
        }
    },

    methods: {
        toggleReadMore() {
            this.readMore = !this.readMore
        },

        confirmDelete() {
            this.deletingPost = this.post
        },

        openReportModal() {
            this.showReportModal = true
        },

        /**
         * Open modal and set the amount to purchase or use the default.
         * @param amount
         */
        openBuyTokensModal(amount = null) {
            this.showBuyTokensModal = true
            this.buyMoreTokensAmount = amount ? amount : this.$page.props.buyTokenDefaultAmount
        },
    },
}
</script>
